import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { employeeAPI } from '../../services/api';
import { toast } from 'react-hot-toast';

const EmployeeAPITest = () => {
  const { user, isAuthenticated } = useAuth();
  const [testResults, setTestResults] = useState([]);
  const [isRunningTests, setIsRunningTests] = useState(false);

  const runEmployeeAPITests = async () => {
    setIsRunningTests(true);
    setTestResults([]);
    
    if (!isAuthenticated) {
      setTestResults([{
        name: 'Authentication Check',
        passed: false,
        message: 'User not authenticated. Please log in first.',
        timestamp: new Date().toISOString()
      }]);
      setIsRunningTests(false);
      toast.error('Please log in to run tests');
      return;
    }
    
    const tests = [
      {
        name: 'Test 1: Employee API Direct Call',
        test: async () => {
          try {
            console.log('🔄 Testing Employee API directly...');
            const response = await employeeAPI.getAllEmployees();
            console.log('📊 Employee API response:', response);
            
            const hasData = response.data && response.data.data;
            const employeeCount = hasData ? response.data.data.length : 0;
            
            return {
              passed: hasData && employeeCount > 0,
              message: `Employee API: ${employeeCount} employees found`,
              data: { 
                employeeCount,
                hasData,
                responseStructure: {
                  message: response.data?.message,
                  hasData: !!response.data?.data,
                  dataType: typeof response.data?.data,
                  dataLength: Array.isArray(response.data?.data) ? response.data.data.length : 'Not an array'
                }
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Employee API Error: ${error.message}`,
              data: { 
                error: error.message,
                errorResponse: error.response?.data,
                status: error.response?.status
              }
            };
          }
        }
      },
      {
        name: 'Test 2: Employee API Response Structure',
        test: async () => {
          try {
            const response = await employeeAPI.getAllEmployees();
            
            const responseStructure = {
              hasMessage: !!response.data?.message,
              hasData: !!response.data?.data,
              messageValue: response.data?.message,
              dataType: typeof response.data?.data,
              isArray: Array.isArray(response.data?.data),
              dataLength: Array.isArray(response.data?.data) ? response.data.data.length : 'Not an array'
            };
            
            const isValidStructure = responseStructure.hasMessage && 
                                   responseStructure.hasData && 
                                   responseStructure.isArray;
            
            return {
              passed: isValidStructure,
              message: `Response Structure: ${isValidStructure ? 'VALID' : 'INVALID'} - Message: ${responseStructure.messageValue}, Data: ${responseStructure.dataLength} items`,
              data: { 
                responseStructure,
                isValidStructure,
                fullResponse: response.data
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Structure Test Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 3: Employee Data Validation',
        test: async () => {
          try {
            const response = await employeeAPI.getAllEmployees();
            const employees = response.data?.data || [];
            
            if (employees.length === 0) {
              return {
                passed: false,
                message: 'No employees found in API response',
                data: { employeeCount: 0 }
              };
            }
            
            const sampleEmployee = employees[0];
            const hasRequiredFields = !!(
              sampleEmployee._id &&
              sampleEmployee.name
              // Note: email is optional for employees
            );
            
            return {
              passed: hasRequiredFields,
              message: `Employee Data: ${hasRequiredFields ? 'VALID' : 'INVALID'} - ${employees.length} employees, Sample: ${sampleEmployee.name}`,
              data: { 
                employeeCount: employees.length,
                hasRequiredFields,
                sampleEmployee: {
                  id: sampleEmployee._id,
                  name: sampleEmployee.name,
                  email: sampleEmployee.email,
                  hasId: !!sampleEmployee._id,
                  hasName: !!sampleEmployee.name,
                  hasEmail: !!sampleEmployee.email
                }
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Data Validation Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 4: API Endpoint Check',
        test: async () => {
          try {
            // Check if the API endpoint is accessible
            const response = await employeeAPI.getAllEmployees();
            const status = response.status;
            const statusText = response.statusText;
            
            return {
              passed: status === 200,
              message: `API Endpoint: ${status === 200 ? 'ACCESSIBLE' : 'ERROR'} - Status: ${status} ${statusText}`,
              data: { 
                status,
                statusText,
                isAccessible: status === 200
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `API Endpoint Error: ${error.message}`,
              data: { 
                error: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
              }
            };
          }
        }
      }
    ];

    const results = [];
    
    for (const test of tests) {
      try {
        console.log(`🧪 Running: ${test.name}`);
        const result = await test.test();
        results.push({
          ...result,
          name: test.name,
          timestamp: new Date().toISOString()
        });
        
        console.log(`✅ ${test.name}: ${result.message}`);
      } catch (error) {
        console.error(`❌ ${test.name}: ${error.message}`);
        results.push({
          name: test.name,
          passed: false,
          message: `Error: ${error.message}`,
          timestamp: new Date().toISOString()
        });
      }
    }
    
    setTestResults(results);
    setIsRunningTests(false);
    
    const passedTests = results.filter(r => r.passed).length;
    const totalTests = results.length;
    
    if (passedTests === totalTests) {
      toast.success(`All ${totalTests} Employee API tests passed! 🎉`);
    } else {
      toast.error(`${passedTests}/${totalTests} tests passed`);
    }
  };

  return (
    <div className="card p-6">
      <h2 className="text-2xl font-bold mb-4">👥 Employee API Test Suite</h2>
      
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-800 mb-2">🎯 Testing Employee API Issues:</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>API Call:</strong> Direct employee API call</li>
          <li>• <strong>Response Structure:</strong> Validates API response format</li>
          <li>• <strong>Data Validation:</strong> Checks employee data fields</li>
          <li>• <strong>Endpoint Access:</strong> Verifies API endpoint is accessible</li>
        </ul>
      </div>
      
      <div className="mb-6">
        <button
          onClick={runEmployeeAPITests}
          disabled={isRunningTests}
          className="btn btn-primary"
        >
          {isRunningTests ? 'Running Tests...' : 'Run Employee API Tests'}
        </button>
      </div>

      {/* Current Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card p-4">
          <h3 className="font-semibold">Authentication</h3>
          <p className="text-sm text-gray-600">
            Status: {isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
          </p>
          <p className="text-sm text-gray-600">
            User: {user?.name || 'N/A'}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">API Status</h3>
          <p className="text-sm text-gray-600">
            Backend: Running
          </p>
          <p className="text-sm text-gray-600">
            Tests: {testResults.length}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Status</h3>
          <p className="text-sm text-gray-600">
            Running: {isRunningTests ? 'Yes' : 'No'}
          </p>
          <p className="text-sm text-gray-600">
            Results: {testResults.length}
          </p>
        </div>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Test Results</h3>
          {testResults.map((result, index) => (
            <div key={index} className={`card p-4 ${result.passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">{result.name}</h4>
                  <p className="text-sm text-gray-600">{result.message}</p>
                  {result.data && (
                    <div className="mt-2 text-xs text-gray-500">
                      <pre>{JSON.stringify(result.data, null, 2)}</pre>
                    </div>
                  )}
                </div>
                <div className={`badge ${result.passed ? 'badge-success' : 'badge-error'}`}>
                  {result.passed ? 'PASS' : 'FAIL'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmployeeAPITest;
